import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getHospitalById,
  updateHospitalStatus,
} from "../../services/superAdmin.service";
import ConfirmModal from "../../components/common/ConfirmModal";

type HospitalStatus = "PENDING" | "ACTIVE" | "SUSPENDED";

const HospitalDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [hospital, setHospital] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState<HospitalStatus>("PENDING");

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // -------------------------
  // Load hospital
  // -------------------------
  const loadHospital = async (showLoader = false) => {
    try {
      if (showLoader) setLoading(true);
      const data = await getHospitalById(id!);
      setHospital(data);
    } catch {
      toast.error("Hospital not found");
      navigate("/dashboard");
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    loadHospital(true);
  }, []);

  useEffect(() => {
    if (hospital?.status) {
      setNewStatus(hospital.status);
    }
  }, [hospital]);

  // -------------------------
  // Confirmed status update
  // -------------------------
  const confirmStatusUpdate = async () => {
    try {
      setUpdating(true);

      await updateHospitalStatus(id!, { status: newStatus });

      toast.success("Hospital status updated successfully");
      setShowConfirmModal(false);

      await loadHospital();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to update status"
      );
    } finally {
      setUpdating(false);
    }
  };

  // -------------------------
  // Click handler
  // -------------------------
  const handleStatusUpdateClick = () => {
    if (!hospital) return;

    if (newStatus === hospital.status) {
      toast.error("Status is already set");
      return;
    }

    if (newStatus === "SUSPENDED") {
      setShowConfirmModal(true);
    } else {
      confirmStatusUpdate();
    }
  };

  const allowedStatuses: HospitalStatus[] =
    hospital?.status === "PENDING"
      ? ["PENDING", "ACTIVE"]
      : hospital?.status === "ACTIVE"
        ? ["ACTIVE", "SUSPENDED"]
        : ["SUSPENDED", "ACTIVE"];

  if (loading) return (
    <div className="flex items-center justify-center p-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
    </div>
  );
  if (!hospital) return null;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button onClick={() => navigate("/dashboard")} className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
              Hospitals
            </button>
            <span className="text-gray-400">/</span>
            <span className="text-sm text-gray-900 font-medium">Details</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{hospital.hospitalName}</h1>
          <p className="text-gray-500 mt-1 flex items-center gap-2">
            Registration No: <span className="font-mono text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded text-xs">{hospital.registrationNumber || "N/A"}</span>
          </p>
        </div>

        {/* Status Manager */}
        <div className="bg-white p-1.5 rounded-lg shadow-sm border border-gray-200 flex items-center gap-2">
          <select
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value as HospitalStatus)}
            className="border-none bg-gray-50 rounded-md text-sm font-medium text-gray-700 focus:ring-0 cursor-pointer hover:bg-gray-100 transition-colors py-1.5 pl-3 pr-8"
          >
            {allowedStatuses.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button
            onClick={handleStatusUpdateClick}
            disabled={updating || newStatus === hospital.status}
            className="bg-primary-600 text-white px-4 py-1.5 rounded-md text-sm font-medium shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {updating ? "Saving..." : "Update Status"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Main Info Card */}
        <div className="md:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2 pb-4 border-b border-gray-50">
            General Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-6">
            <Info label="Official Email" value={hospital.officialEmail} />
            <Info label="Official Phone" value={hospital.officialPhone} />
            <Info label="Domain Name" value={hospital.domainName} />
            <Info label="Database Name" value={hospital.dbName} />
            <div className="col-span-2">
              <Info
                label="Full Address"
                value={`${hospital.addressline1 || ""} ${hospital.addressline2 || ""} ${hospital.city || ""} ${hospital.country || ""} ${hospital.postcode || ""}`}
              />
            </div>
          </div>
        </div>

        {/* Side Card - Status & Meta */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Current Status</h2>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 font-medium">System Access</span>
              <StatusBadge status={hospital.status} />
            </div>

            <div className="mt-6 pt-6 border-t border-gray-50">
              <p className="text-xs text-gray-400">
                Last updated: {new Date().toLocaleDateString()}
                {/* Placeholder date since not provided in data */}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CONFIRM MODAL */}
      <ConfirmModal
        isOpen={showConfirmModal}
        title="Suspend Hospital Access"
        message="Are you sure you want to suspend this hospital? All administrative users will be immediately blocked from accessing the system."
        confirmText="Yes, Suspend"
        cancelText="Cancel"
        loading={updating}
        onConfirm={confirmStatusUpdate}
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  );
};

const Info = ({ label, value }: { label: string; value: string }) => (
  <div className="group">
    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
    <p className="text-sm font-medium text-gray-900 group-hover:text-primary-700 transition-colors break-words">{value || "—"}</p>
  </div>
);

const StatusBadge = ({ status }: { status: string }) => {
  let classes = "bg-gray-100 text-gray-600";
  switch (status?.toUpperCase()) {
    case "ACTIVE": classes = "bg-green-100 text-green-700"; break;
    case "PENDING": classes = "bg-yellow-100 text-yellow-800"; break;
    case "SUSPENDED": classes = "bg-red-100 text-red-700"; break;
  }
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${classes}`}>
      {status}
    </span>
  );
}

export default HospitalDetails;
